/* Seite „Eigenschaften“, Teil 1: Objekt und Entity (Auswahl, Position/Größe, Aktionen, Aushöhlen, Entity-Editor).
 Texturen und Flächen: FacePart.tsx. */
import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import Cmd from '../../model/cmds.ts';
import EntDefs from '../../model/entdefs.ts';
import EntSpec from '../../model/entspec.ts';
import { fieldInfo } from '../../model/fieldinfo.ts';
import Keys from '../../keys.ts';
import { bboxFromInputs, selectionEntity, sizeOf, toggleFlag, unknownFlagBits } from '../../model/props.ts';
import type { BBox, Doc, Entity, Sel } from '../../model/types.ts';
import { useT } from '../hooks.ts';
import { ClassSelect } from '../class-options.tsx';
import { ensureDatalist } from '../datalists.ts';
import { AssetField } from '../assets/KindSelect.tsx';
import { addWad } from '../../model/assets.ts';
import { createNotifier, useNotifier } from '../notifier.ts';
import type { Notifier } from '../notifier.ts';
import { CommitInput, NumCommit } from '../texture/widgets.tsx';
import { FacePart } from './FacePart.tsx';
import { FgdField } from './FgdField.tsx';
import type { FgdHost } from './FgdField.tsx';
import type { FaceApp, FaceShared } from './FacePart.tsx';

export interface PropsApp extends FaceApp, FgdHost {
  sel: Sel; doc: Doc; bsp: unknown; decompNull?: string; lock: boolean; clipboard: unknown; activeTex: string;
  viewDoc(): Doc; selBBox(): BBox | null;
  status(msg: string): void; commit(doc: Doc, label: string, sel?: Sel): void;
  decompileBsp(): void; carve(): void; merge(): void; dup(): void; del(): void; copy(): void; paste(): void;
  rotate90(axis: number, dir: number): void; mirror(axis: number): void;
  toEntity(cls: string): void; toWorld(): void; openSettings(tab: string): void;
  soundFor(e: Entity): { name: string; dur: number } | null; soundPlaying: Map<string, unknown>; soundVol(e: Entity): number;
  playSound(e: Entity): boolean; stopSound(e: Entity): void; openSoundFiles(): void;
}
/** Zustand über das Neuzeichnen hinaus */
export interface PropsShared extends FaceShared { hollow: { t: number; tex: string } }
const HELP_KEYS: [string, string][] = [['toolBox', 'pn.h.box'], ['toolSelect', 'pn.h.select'], ['toolEntity', 'pn.h.entity'], ['toolClip', 'pn.h.clip'], ['toolVertex', 'pn.h.vertex'], ['dup', 'pn.h.dup'], ['rotate', 'pn.h.rotate'], ['fit', 'pn.h.fit'], ['snap', 'pn.h.snap'], ['hide', 'pn.h.hide'], ['isolate', 'pn.h.isolate'], ['group', 'pn.h.group'], ['find', 'pn.h.find']];
/** Klassen, deren Feld model ein Sprite ist (sonst ein Modell, dafür gibt es keine Auswahl) */
const SPRITE_CLASSES = /^(env_sprite|env_glow|cycler_sprite)$/;
const AXES: [string, number][] = [['X', 0], ['Y', 1], ['Z', 2]];

function Btn(props: { onClick: () => void; children: React.ReactNode; primary?: boolean; title?: string }) {
  return <button className={'btn sm' + (props.primary ? ' primary' : '')} title={props.title || ''} onClick={props.onClick}>{props.children}</button>;
}

/** Position und Größe; liest die Box live (auch während des Ziehens), ohne dass die restliche Seite neu gezeichnet wird */
function BBoxFields(props: { app: PropsApp; live: Notifier }) {
  const t = useT();
  useNotifier(props.live);
  const { app } = props, bb = app.selBBox();
  if (!bb) return null;
  const apply = (min: number[], max: number[]) => {
    const nb = bboxFromInputs(min, max);
    if (!nb) { app.status(t('pn.size_invalid')); props.live.notify(); return; }
    app.commit(Cmd.setBBox(app.doc, app.sel, nb, app.lock), t('cmd.resize'));
  };
  const setAt = (which: 'min' | 'max', i: number, v: number) => { const mn = bb.min.slice(), mx = bb.max.slice(); (which === 'min' ? mn : mx)[i] = v; apply(mn, mx); };
  const round = (v: number) => Math.round(v * 100) / 100;
  return (
    <>
      <h3>{t('pn.pos_size')}</h3>
      <div className="fields">
        <label>Min</label>
        <div className="xyz">{AXES.map(([n, i]) => <NumCommit key={n} digits={2} aria-label={'Min ' + n} value={round(bb.min[i])} onCommit={(v) => setAt('min', i, v)} />)}</div>
        <label>Max</label>
        <div className="xyz">{AXES.map(([n, i]) => <NumCommit key={n} digits={2} aria-label={'Max ' + n} value={round(bb.max[i])} onCommit={(v) => setAt('max', i, v)} />)}</div>
        <label>{t('pn.size')}</label>
        <span className="small" data-f="size">{sizeOf(bb).join(' × ')}</span>
      </div>
    </>
  );
}

function MapSettings(props: { app: PropsApp; doc: Doc }) {
  const t = useT();
  const { app, doc } = props, w = doc.ents[0];
  const cov = EntSpec.covered(w.kv.classname), extra = Object.keys(w.kv).filter((k) => !cov.has(k));
  const help = HELP_KEYS.filter((x) => Keys.get(x[0]).length).map((x) => Keys.fmt(Keys.get(x[0])[0]) + ': ' + t(x[1])).join(' · ');
  return (
    <>
      <h3>{t('pn.map')}</h3>
      <SpecEditor app={app} ent={w} />
      <details className="gen" open={extra.length > 0 || undefined}>
        <summary>{t('pn.all_fields') + (extra.length ? ' · ' + t('pn.more_fields', { count: extra.length }) : '')}</summary>
        <div className="body"><KvEditor app={app} ent={w} skip={cov} withClass={false} /></div>
      </details>
      <p className="small">{t('pn.nothing_sel')}</p>
      <h3>{t('pn.quickhelp')}</h3>
      <p className="small">{t('pn.help_head') + ' ' + help + ' · ' + t('pn.help_tail')}</p>
      <button className="btn sm" title={t('pn.all_keys_tip')} onClick={() => app.openSettings('keys')}>{t('pn.all_keys')}</button>
    </>
  );
}
/** Zeile mit Beschriftung; info: Erklärung als (i) neben der Beschriftung */
const Row = (p: { label: string; info?: string; children: React.ReactNode }) => <><label>{p.label}{p.info && <> <span className="info-i" tabIndex={0} role="img" aria-label={p.info} title={p.info}>i</span></>}</label>{p.children}</>;

function BspNote(props: { app: PropsApp }) {
  const t = useT();
  const { app } = props;
  return (
    <>
      <div className="warnbox">{t('pn.bsp_mode')}</div>
      <h3>{t('cmd.decompile')}</h3>
      <p className="small">{t('pn.decomp_note')}</p>
      <div className="row">
        <input type="text" className="inp" style={{ width: 90 }} title={t('pn.decomp_null')} aria-label={t('pn.decomp_null')} defaultValue={app.decompNull || 'NULL'} onChange={(e) => { app.decompNull = e.target.value.trim() || 'NULL'; }} />
        <Btn primary onClick={() => app.decompileBsp()}>{t('cmd.decompile')}</Btn>
      </div>
    </>
  );
}

function Actions(props: { app: PropsApp }) {
  const t = useT();
  const { app } = props;
  return (
    <>
      <h3>{t('pn.actions')}</h3>
      <div className="row">
        <Btn onClick={() => app.dup()}>{t('cmd.duplicate')}</Btn><Btn onClick={() => app.del()}>{t('cmd.delete')}</Btn>
        {app.sel.b.size > 0 && <Btn title={t('pn.carve_tip')} onClick={() => app.carve()}>{t('cmd.carve')}</Btn>}
        {app.sel.b.size > 1 && <Btn title={t('pn.merge_tip')} onClick={() => app.merge()}>{t('cmd.merge')}</Btn>}
        <Btn onClick={() => app.copy()}>{t('ctx.copy')}</Btn><Btn onClick={() => app.paste()} title={app.clipboard ? '' : t('ctx.copy_first')}>{t('cmd.paste')}</Btn>
      </div>
      <div className="row">
        <span className="lbl-s">{t('pn.rotate90')}</span>
        {[['Z', 2], ['X', 0], ['Y', 1]].map(([n, a]) => <Btn key={n} onClick={() => app.rotate90(a as number, 1)}>{n}</Btn>)}
        <span className="lbl-s">{t('cmd.mirror')}</span>
        {AXES.map(([n, a]) => <Btn key={n} onClick={() => app.mirror(a)}>{n}</Btn>)}
      </div>
    </>
  );
}

function Hollow(props: { app: PropsApp; shared: PropsShared }) {
  const t = useT();
  const { app } = props, ht = props.shared.hollow;
  const run = () => {
    const r = Cmd.hollow(app.doc, app.sel, ht.t, ht.tex || app.activeTex);
    if (!r) { app.status(t('pn.hollow_fail')); return; }
    app.commit(r.doc, t('cmd.hollow'), r.sel); app.status(t('pn.hollowed', { count: r.sel.b.size, t: ht.t }));
  };
  return (
    <>
      <h3>{t('cmd.hollow')}</h3>
      <div className="row">
        <span className="lbl-s">{t('pn.wall')}</span><NumCommit width="70px" aria-label={t('pn.wall')} value={ht.t} onCommit={(v) => { ht.t = Math.max(1, v); }} />
        <span className="lbl-s">{t('pn.inner_tex')}</span>
        <CommitInput list="dl-tex" aria-label={t('pn.inner_tex')} value={ht.tex || app.activeTex} onCommit={(v) => { ht.tex = v.trim(); }} />
      </div>
      <div className="row"><Btn primary title={t('pn.hollow_tip')} onClick={run}>{t('cmd.hollow')}</Btn></div>
      <p className="small">{t('pn.hollow_note')}</p>
    </>
  );
}

/** Typisierte Eigenschaften nach EntSpec: Flags, Auswahlfelder, Farben, Textfelder */
function SpecEditor(props: { app: PropsApp; ent: Entity }) {
  const t = useT();
  const { app, ent } = props, sp = EntSpec.get(ent.kv.classname);
  if (!sp) return null;
  const setv = (k: string, v: string, lbl?: string) => app.commit(v === '' ? Cmd.delKV(app.doc, ent.id, k) : Cmd.setKV(app.doc, ent.id, k, v), lbl || t('cmd.property', { key: k }));
  const cur = parseInt(ent.kv.spawnflags, 10) || 0, extraBits = unknownFlagBits(cur, sp.f);
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {sp.d && <p className="small">{sp.raw ? sp.d : t(sp.d)}</p>}
      {sp.f.length > 0 && (
        <>
          <div className="flaggrid">
            {sp.f.map((f) => (
              <label key={f[0]} className="chk" title={t('pn.spawnflag_bit', { bit: f[0] })}>
                <input type="checkbox" checked={!!(cur & f[0])} onChange={(e) => setv('spawnflags', String(toggleFlag(cur, f[0], e.target.checked)), t('cmd.spawnflag'))} />{f[2] ? f[1] : t(f[1])}
              </label>
            ))}
          </div>
          {extraBits ? <p className="small">{t('pn.extra_bits', { bits: extraBits, total: cur })}</p> : cur ? <p className="small">{'spawnflags = ' + cur}</p> : null}
        </>
      )}
      {sp.k.length > 0 && (
        <div className="fields">
          {sp.k.map(([key, label, type, extra, dflt, raw]) => {
            const tx = (x: string): string => (raw ? x : t(x));
            const v = ent.kv[key];
            let ctl: React.ReactNode;
            if (type === 'c') {
              const opts = (extra || []) as [string, string][];
              ctl = (
                <select className="sel" title={key} aria-label={key} value={v ?? ''} onChange={(e) => setv(key, e.target.value)}>
                  <option value="">{t('pn.default')}</option>
                  {opts.map((c) => <option key={c[0]} value={c[0]}>{c[0] + ' · ' + tx(c[1])}</option>)}
                  {v !== undefined && v !== '' && !opts.some((c) => c[0] === v) && <option value={v}>{v + ' · ' + t('pn.custom_value')}</option>}
                </select>
              );
            } else if (type === 'col') {
              ctl = (
                <div className="row">
                  <input type="color" aria-label={key} value={EntSpec.hexOf(v || '128 128 128')} title={key + ' = ' + (v || t('pn.unset'))} onChange={(e) => setv(key, EntSpec.rgbOf(e.target.value))} />
                  <span className="small">{v || t('pn.unset')}</span>
                  {v ? <button className="btn sm x" title={t('pn.remove_value')} onClick={() => setv(key, '')}>×</button> : null}
                </div>
              );
            } else if (type === 'wad') {
              ctl = (
                <div style={{ display: 'grid', gap: 4, minWidth: 0 }}>
                  <CommitInput aria-label={key} value={v ?? ''} onCommit={(nv) => setv(key, nv.trim())} />
                  {app.assets && <AssetField host={app.assets} kind="wad" ariaLabel={t('pn.add_wad')} placeholder={t('pn.add_wad')} value="" onCommit={(nv) => { const n = nv.trim(); if (n) setv(key, addWad(v ?? '', n)); }} />}
                </div>
              );
            } else if (type === 'fgd') {
              ctl = <FgdField host={app} value={v ?? ''} onChange={(nv) => setv(key, nv)} />;
            } else if (type === 'sky' && app.assets) {
              ctl = <AssetField host={app.assets} kind="skybox" ariaLabel={key} value={v ?? ''} format={(e) => e.key} onCommit={(nv) => setv(key, nv.trim())} onFree={(nv) => { setv(key, nv.trim()); void app.assets?.autoLoad(); }} />;
            } else if (app.assets && (type === 'snd' || (type === 'spr' && (raw || (key === 'model' && SPRITE_CLASSES.test(ent.kv.classname)))))) {
              const snd = type === 'snd';
              ctl = <AssetField host={app.assets} kind={snd ? 'sound' : 'sprite'} ariaLabel={key} value={v ?? ''} format={(e) => (snd ? e.key : 'sprites/' + e.key)} onCommit={(nv) => setv(key, nv.trim())} />;
            } else {
              ctl = <CommitInput aria-label={key} list={type === 'T' || type === 't' ? 'dl-targets' : undefined} placeholder={dflt !== undefined ? t('pn.default_val', { v: dflt }) : ''} value={v ?? ''} onCommit={(nv) => setv(key, nv.trim())} />;
            }
            const fi = fieldInfo(key);
            return <Row key={key} label={tx(label)} info={fi ? t(fi) : undefined}>{ctl}</Row>;
          })}
        </div>
      )}
    </div>
  );
}

function KvEditor(props: { app: PropsApp; ent: Entity; skip?: Set<string>; withClass: boolean }) {
  const t = useT();
  const { app, ent, skip } = props;
  const nk = useRef<HTMLInputElement>(null), nv = useRef<HTMLInputElement>(null);
  const add = () => {
    const k = (nk.current?.value || '').trim();
    if (k && k !== 'classname') app.commit(Cmd.setKV(app.doc, ent.id, k, nv.current?.value || ''), t('cmd.add_field'));
  };
  const keys = Object.keys(ent.kv).filter((k) => k !== 'classname' && !(skip && skip.has(k)));
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {props.withClass && <ClassField app={app} ent={ent} />}
      {keys.map((k) => (
        <div className="kv" key={k}>
          <CommitInput aria-label={t('pn.new_field') + ' ' + k} value={k} onCommit={(nkey) => { nkey = nkey.trim(); if (nkey && nkey !== k) app.commit(Cmd.renameKV(app.doc, ent.id, k, nkey), t('cmd.rename_field')); }} />
          <CommitInput aria-label={k} value={ent.kv[k]} onCommit={(v) => app.commit(Cmd.setKV(app.doc, ent.id, k, v), t('cmd.property', { key: k }))} />
          <button className="btn sm x" title={t('pn.remove_field')} onClick={() => app.commit(Cmd.delKV(app.doc, ent.id, k), t('cmd.delete_field'))}>×</button>
        </div>
      ))}
      <div className="kv">
        <input type="text" className="inp" ref={nk} spellCheck={false} placeholder={t('pn.new_field')} aria-label={t('pn.new_field')} />
        <input type="text" className="inp" ref={nv} spellCheck={false} placeholder={t('find.value')} aria-label={t('find.value')} onKeyDown={(e) => { if (e.key === 'Enter') add(); }} onBlur={add} />
        <button className="btn sm x" title={t('cmd.add_field')} onClick={add}>+</button>
      </div>
    </div>
  );
}

function ClassField(props: { app: PropsApp; ent: Entity }) {
  const { app, ent } = props;
  const t = useT();
  return (
    <div className="fields">
      <label>classname</label>
      <CommitInput aria-label="classname" list="dl-cls" value={ent.kv.classname} onCommit={(v) => { v = v.trim(); if (v) app.commit(Cmd.setKV(app.doc, ent.id, 'classname', v), t('cmd.class')); }} />
    </div>
  );
}

function EntityEditor(props: { app: PropsApp; ent: Entity }) {
  const t = useT();
  const { app, ent } = props, sp = EntSpec.get(ent.kv.classname);
  const cov = EntSpec.covered(ent.kv.classname), extra = Object.keys(ent.kv).filter((k) => !cov.has(k));
  return (
    <>
      <h3>{t('pn.entity_h', { cls: ent.kv.classname })}</h3>
      {sp ? (
        <>
          <ClassField app={app} ent={ent} />
          <SpecEditor app={app} ent={ent} />
          <details className="gen" open={extra.length > 0 || undefined}>
            <summary>{t('pn.all_fields') + (extra.length ? ' · ' + t('pn.more_fields', { count: extra.length }) : '')}</summary>
            <div className="body"><KvEditor app={app} ent={ent} skip={cov} withClass={false} /></div>
          </details>
        </>
      ) : <KvEditor app={app} ent={ent} withClass />}
    </>
  );
}

function SoundPreview(props: { app: PropsApp; ent: Entity; refresh: () => void }) {
  const t = useT();
  const { app, ent } = props, snd = app.soundFor(ent), playing = app.soundPlaying.has(ent.id), rad = EntDefs.soundRadius(ent.kv);
  const info = (snd ? '✓ ' + snd.name + ' · ' + snd.dur.toFixed(1) + ' s' : t('pn.sound_missing', { msg: ent.kv.message || '' }))
    + ' · ' + t('pn.hearing', { range: rad ? t('pn.hearing_units', { count: rad }) : t('pn.everywhere') }) + ' · ' + t('pn.volume', { v: Math.round(app.soundVol(ent) * 10) });
  return (
    <>
      <h3>{t('pn.sound_preview')}</h3>
      <p className="small">{info}</p>
      <div className="row">
        <Btn primary={!!snd} onClick={() => { if (playing) app.stopSound(ent); else if (!app.playSound(ent)) app.status(t('pn.load_sound_first')); props.refresh(); }}>{playing ? t('pn.stop') : t('pn.play')}</Btn>
        <Btn onClick={() => app.openSoundFiles()}>{t('pn.load_sounds')}</Btn>
      </div>
    </>
  );
}

function BrushEntity(props: { app: PropsApp; ent: Entity | null }) {
  const t = useT();
  const { app, ent } = props;
  const dflt = ent && ent.brushes.length ? ent.kv.classname : 'func_wall';
  return (
    <>
      <h3>{t('pn.brush_entity')}</h3>
      <BrushEntityRow key={dflt} app={app} ent={ent} dflt={dflt} />
    </>
  );
}

/** Eigener Zustand für die gewählte Klasse; neu aufgebaut, wenn sich die Klasse der Auswahl ändert */
function BrushEntityRow(props: { app: PropsApp; ent: Entity | null; dflt: string }) {
  const t = useT();
  const { app, ent } = props;
  const [cls, setCls] = useState(props.dflt);
  return (
    <div className="row">
      <ClassSelect kind="brush" value={cls} ariaLabel={t('pn.brush_entity')} onPick={setCls} onFree={setCls} />
      <Btn primary onClick={() => app.toEntity(cls)}>{t('pn.assign')}</Btn>
      {ent && <Btn title={t('pn.to_world_tip')} onClick={() => app.toWorld()}>{t('cmd.to_world')}</Btn>}
    </div>
  );
}

export function PropsPanel(props: { app: PropsApp; shared: PropsShared; notifier: Notifier; live: Notifier }) {
  const t = useT();
  useNotifier(props.notifier);
  const { app } = props, doc = app.viewDoc(), sel = app.sel;
  const bs = Cmd.selBrushes(doc, sel), es = Cmd.selEnts(doc, sel), ent = selectionEntity(doc, sel);
  useEffect(() => {
    ensureDatalist('dl-cls', EntDefs.list.map((d) => d.c));
    const names = new Set<string>(); app.doc.ents.forEach((e) => { if (e.kv.targetname) names.add(e.kv.targetname); });
    ensureDatalist('dl-targets', Array.from(names).sort());
  });
  const head = (
    <>
      {!!app.bsp && <BspNote app={app} />}
    </>
  );
  if (!bs.length && !es.length) return <>{head}<MapSettings app={app} doc={doc} /></>;
  const parts: string[] = [];
  if (bs.length) parts.push(t('pn.n_brushes', { count: bs.length }));
  if (es.length) parts.push(t('pn.n_ents', { count: es.length }));
  return (
    <>
      {head}
      <div className="big">{parts.join(' + ')}</div>
      {bs.length > 0 && <BBoxFields app={app} live={props.live} />}
      <Actions app={app} />
      {bs.length > 0 && <Hollow app={app} shared={props.shared} />}
      <FacePart app={app} shared={props.shared} refresh={props.notifier.notify} />
      {ent && <EntityEditor app={app} ent={ent} />}
      {ent && ent.kv.classname === 'ambient_generic' && <SoundPreview app={app} ent={ent} refresh={props.notifier.notify} />}
      {bs.length > 0 && <BrushEntity app={app} ent={ent} />}
    </>
  );
}

export function mountPropsPanel(el: HTMLElement, app: PropsApp, shared: PropsShared): { refresh(): void; live(): void; unmount(): void } {
  const notifier = createNotifier(), live = createNotifier();
  const root = createRoot(el);
  el.textContent = '';
  root.render(<PropsPanel app={app} shared={shared} notifier={notifier} live={live} />);
  return { refresh: notifier.notify, live: live.notify, unmount: () => root.unmount() };
}
